import React, { useState, useEffect } from 'react';
import { useSession } from '../../../Utils/SessionContext';
import { usePopupClose } from '../../../Utils/usePopupClose';
import useSkillsAutocomplete from '../../../Utils/useSkillsAutocomplete';
import ComboBoxSelect from '../../../CreateEvaluationComponents/Shards/ComboBoxSelect';
import InputList from '../../../CreateEvaluationComponents/Shards/InputList';
import { updateQuestionnaireQuestions } from '../../Utils/QuestionUpdater';
import { 
  createQuestionFromTemplate, 
  questionTypeRequiresOptions 
} from '../../Utils/QuestionTemplates';
import toast from 'react-hot-toast';

/**
 * AddQuestionnaireQuestion Component
 * Modal for adding new questionnaire questions
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Close handler
 * @param {string} questionnaireId - Questionnaire ID for API call
 * @param {array} allQuestions - Full questions array
 * @param {function} onSuccess - Callback after successful save
 */
const AddQuestionnaireQuestion = ({
  isOpen,
  onClose,
  questionnaireId,
  allQuestions,
  onSuccess
}) => {
  const { user } = useSession();
  const [isSaving, setIsSaving] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  
  // Skills autocomplete hook
  const { skillsOptions, isLoadingSkills, searchSkills, clearSkills } = useSkillsAutocomplete();
  
  // Use popup close hook - close on ESC key only, not on click outside
  const popupRef = usePopupClose(isOpen, onClose, false, true);

  // Handle mounting/unmounting with animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to ensure DOM is ready for animation
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  
  // State for new question
  const [newQuestion, setNewQuestion] = useState({
    questionText: '',
    inRelationTo: [],
    options: [],
    type: ''
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setNewQuestion({
        questionText: '',
        inRelationTo: [],
        options: [],
        type: ''
      });
    }
  }, [isOpen]);

  // Helper function to generate next question ID
  const getNextQuestionId = () => {
    if (!allQuestions || allQuestions.length === 0) return '1';
    const maxId = Math.max(...allQuestions.map(q => parseInt(q.id) || 0));
    return (maxId + 1).toString();
  };

  // Helper function to generate UUID
  const generateUUID = () => {
    return crypto.randomUUID();
  };

  // Convert skills array to ComboBoxSelect format
  const skillsAsOptions = newQuestion.inRelationTo.map(skill => ({
    value: skill,
    label: skill
  }));

  // Convert options array to InputList format
  const optionsAsItems = newQuestion.options.map(opt => ({
    title: opt,
    subtitle: ''
  }));

  // Handle question type change
  const handleTypeChange = (e) => {
    setNewQuestion({ ...newQuestion, type: e.target.value });
  };

  // Handle skills change
  const handleSkillsChange = (selectedOptions) => {
    const skills = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setNewQuestion({ ...newQuestion, inRelationTo: skills });
  };

  // Handle creating new skill
  const handleCreateSkill = (inputValue) => {
    const newSkills = [...newQuestion.inRelationTo, inputValue];
    setNewQuestion({ ...newQuestion, inRelationTo: newSkills });
  };

  // Handle options change
  const handleOptionsChange = (items) => {
    const options = items.map(item => item.title);
    setNewQuestion({ ...newQuestion, options });
  };

  // Handle question text change
  const handleQuestionTextChange = (e) => {
    setNewQuestion({ ...newQuestion, questionText: e.target.value });
  };

  // Validate form
  const validateForm = () => {
    if (!newQuestion.type) {
      toast.error('Please select a question type');
      return false;
    }

    if (!newQuestion.questionText.trim()) {
      toast.error('Question text is required');
      return false;
    }

    if (!newQuestion.inRelationTo || newQuestion.inRelationTo.length === 0) {
      toast.error('At least one skill must be selected');
      return false;
    }

    // Validate options for choice questions using centralized helper
    if (questionTypeRequiresOptions(newQuestion.type, 'questionnaire')) {
      if (!newQuestion.options || newQuestion.options.length < 2) {
        toast.error('Choice questions must have at least 2 options');
        return false;
      }
    }

    return true;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);

    try {
      // Create question from template with user's input
      const questionToAdd = createQuestionFromTemplate(
        newQuestion.type,
        'questionnaire',
        {
          id: getNextQuestionId(),
          uuid: generateUUID(),
          questionText: newQuestion.questionText,
          inRelationTo: newQuestion.inRelationTo,
          // Type-specific overrides
          ...(questionTypeRequiresOptions(newQuestion.type, 'questionnaire') && {
            options: newQuestion.options,
          }),
        }
      );

      // Add to questions array
      const updatedQuestions = [...allQuestions, questionToAdd];

      // Use utility function to update questions
      await updateQuestionnaireQuestions(questionnaireId, updatedQuestions, user);

      toast.success('Question added successfully!');
      
      // Close modal and trigger success callback
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error adding question:', error);
      toast.error('Failed to add question. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!shouldRender) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay with blur */}
      <div 
        className={`fixed inset-0 bg-white transition-all duration-300 ${
          isAnimating ? 'backdrop-blur-sm bg-opacity-40' : 'bg-opacity-0'
        }`}
        onClick={onClose}
      />
      
      {/* Sliding Panel */}
      <div className="fixed inset-y-0 right-0 flex max-w-full">
        <div 
          ref={popupRef}
          className={`w-screen max-w-4xl transform transition-all duration-300 ease-in-out ${
            isAnimating ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="h-full flex flex-col bg-white shadow-2xl overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-20">
              <h2 className="text-2xl font-bold text-gray-900">Add New Questionnaire Question</h2>
              <button 
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 overflow-visible">
              {/* Question Type Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Question Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={newQuestion.type}
                  onChange={handleTypeChange}
                  className="select-custom w-full py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                >
                  <option value="">Select a question type...</option>
                  <option value="multiple-answer">Multiple Choice</option>
                  <option value="single-answer">Single Choice</option>
                  <option value="long-text">Long Text</option>
                  <option value="short-text">Short Text</option>
                  <option value="audio-answer">Audio</option>
                  <option value="video-answer">Video</option>
                </select>
              </div>

              {/* Show form fields only after type is selected */}
              {newQuestion.type && (
                <>
                  {/* Skills Covered */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Skills Covered <span className="text-red-500">*</span>
                    </label>
                    <ComboBoxSelect
                      value={skillsAsOptions}
                      onChange={handleSkillsChange}
                      options={skillsOptions}
                      placeholder="Type to search or add custom skills..."
                  onInputChange={(inputValue, action) => {
                    if (action.action === 'input-change') {
                      if (inputValue && inputValue.length >= 2) {
                        searchSkills(inputValue);
                      } else {
                        clearSkills();
                      }
                    }
                  }}
                      isLoading={isLoadingSkills}
                      onCreateOption={handleCreateSkill}
                      isMulti={true}
                    />
                  </div>

                  {/* Question Text */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Question Text <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={newQuestion.questionText}
                      onChange={handleQuestionTextChange}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white"
                      placeholder="Enter the question text..."
                    />
                  </div>

                  {/* Options - Only for choice questions */}
                  {questionTypeRequiresOptions(newQuestion.type, 'questionnaire') && (
                    <div>
                      <InputList
                        label="Options (minimum 2 required)"
                        items={optionsAsItems}
                        onItemsChange={handleOptionsChange}
                        placeholder="Enter an option..."
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-center gap-4 z-20">
              <button
                onClick={onClose}
                disabled={isSaving}
                className="px-8 py-3 bg-white text-gray-700 border-2 border-gray-300 font-semibold rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                CANCEL
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !newQuestion.type}
                className="px-8 py-3 bg-[#33C6B6] text-white font-semibold rounded-lg hover:bg-[#2db5a6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    SAVING...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    ADD QUESTION
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddQuestionnaireQuestion;
